/**
 * SynapseWorkerBridge - v1.0
 * Main thread bridge for communicating with the Synapse worker
 */

import type {
  WorkerMessage,
  WorkerResponse,
  WorkerError,
  MessageType,
  WorkerStatus,
  TaskEvent,
} from '../types/worker-messages';

import { createMessage, isWorkerError } from '../types/worker-messages';

// ============================================================================
// Types
// ============================================================================

type PendingRequest = {
  resolve: (response: WorkerResponse<any>) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
};

type SubscriptionCallback = (event: any) => void;

interface Subscription {
  id: string;
  callback: SubscriptionCallback;
  unsubscribe: () => void;
}

type StatusListener = (status: WorkerStatus) => void;
type EventListener = (event: any) => void;

// ============================================================================
// Bridge Class
// ============================================================================

class SynapseWorkerBridge {
  private worker: Worker | null = null;
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private subscriptions: Map<string, Subscription> = new Map();
  private statusListeners: Set<StatusListener> = new Set();
  private eventListeners: Set<EventListener> = new Set();
  private _status: WorkerStatus = 'initializing';
  private heartbeatTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly REQUEST_TIMEOUT = 30000;
  private readonly HEARTBEAT_TIMEOUT = 60000;

  // ============================================================================
  // Public API
  // ============================================================================

  get status(): WorkerStatus {
    return this._status;
  }

  /**
   * Initialize the worker
   */
  async initialize(): Promise<void> {
    if (this.worker) return;

    try {
      // Create worker using Vite's worker import pattern
      this.worker = new Worker(
        new URL('../workers/synapse.worker.ts', import.meta.url),
        { type: 'module' }
      );

      this.worker.onmessage = this.handleWorkerMessage.bind(this);
      this.worker.onerror = this.handleWorkerError.bind(this);

      // Wait for worker ready signal
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Worker initialization timeout'));
        }, 10000);

        const handler = (event: MessageEvent) => {
          if (event.data?.payload?.type === 'worker:ready') {
            clearTimeout(timeout);
            this.worker?.removeEventListener('message', handler);
            resolve();
          }
        };

        this.worker?.addEventListener('message', handler);
      });

      // Set status to ready BEFORE sending init (send() checks status)
      this.setStatus('ready');

      // Send init message
      await this.send('worker:init', {});

      this.startHeartbeatMonitor();
    } catch (error) {
      this.setStatus('error');
      throw error;
    }
  }

  /**
   * Shutdown the worker gracefully
   */
  async shutdown(): Promise<void> {
    this.stopHeartbeatMonitor();

    // Cancel all pending requests
    this.pendingRequests.forEach((request, id) => {
      clearTimeout(request.timeout);
      request.reject(new Error('Worker shutdown'));
    });
    this.pendingRequests.clear();

    // Unsubscribe all subscriptions
    for (const [id] of this.subscriptions) {
      await this.send('unsubscribe', { subscriptionId: id }).catch(() => {});
    }
    this.subscriptions.clear();

    // Send shutdown message
    if (this.worker && this._status === 'ready') {
      try {
        await this.send('worker:shutdown', {});
      } catch {
        // Ignore errors during shutdown
      }
    }

    // Terminate worker
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    this.setStatus('terminated');
  }

  /**
   * Send a message to the worker and wait for response
   */
  async send<T = any>(type: MessageType, payload: any): Promise<WorkerResponse<T>> {
    if (!this.worker || this._status !== 'ready') {
      // Auto-initialize if not ready
      if (!this.worker) {
        await this.initialize();
      } else {
        throw new Error(`Worker not ready: ${this._status}`);
      }
    }

    const message = createMessage(type, payload);

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(message.id);
        reject(new Error(`Request timeout: ${type}`));
      }, this.REQUEST_TIMEOUT);

      this.pendingRequests.set(message.id, { resolve, reject, timeout });
      this.worker!.postMessage(message);
    });
  }

  /**
   * Subscribe to task events
   */
  async subscribe(
    type: 'tasks' | 'task',
    callback: SubscriptionCallback,
    taskId?: string
  ): Promise<Subscription> {
    const messageType = type === 'tasks' ? 'subscribe:tasks' : 'subscribe:task';
    const payload = type === 'task' ? { taskId } : {};

    const response = await this.send<{ subscriptionId: string }>(messageType, payload);

    if (response.type === 'error') {
      const errorPayload = response.payload as unknown as WorkerError;
      throw new Error(errorPayload.message);
    }

    const subscriptionId = (response.payload as { subscriptionId: string }).subscriptionId;

    const subscription: Subscription = {
      id: subscriptionId,
      callback,
      unsubscribe: async () => {
        this.subscriptions.delete(subscriptionId);
        await this.send('unsubscribe', { subscriptionId }).catch(() => {});
      },
    };

    this.subscriptions.set(subscriptionId, subscription);
    return subscription;
  }

  /**
   * Listen for status changes
   */
  onStatusChange(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  /**
   * Listen for all worker events
   */
  onEvent(listener: EventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  private handleWorkerMessage(event: MessageEvent<WorkerResponse<any>>): void {
    const response = event.data;

    // Handle events (subscription updates, heartbeats, etc.)
    if (response.type === 'event') {
      this.handleWorkerEvent(response.payload);
      return;
    }

    // Handle responses to pending requests
    const pending = this.pendingRequests.get(response.id);
    if (pending) {
      clearTimeout(pending.timeout);
      this.pendingRequests.delete(response.id);
      pending.resolve(response);
    }
  }

  private handleWorkerEvent(payload: any): void {
    // Notify all event listeners
    this.eventListeners.forEach((listener) => listener(payload));

    // Handle specific event types
    switch (payload.type) {
      case 'worker:heartbeat':
        this.resetHeartbeatMonitor();
        break;

      case 'subscription:event':
        // Route to subscription callback
        const subscription = this.subscriptions.get(payload.subscriptionId);
        if (subscription) {
          subscription.callback(payload.event);
        }
        break;

      case 'ws:connected':
      case 'ws:disconnected':
      case 'ws:error':
        // WebSocket status events
        break;
    }
  }

  private handleWorkerError(error: ErrorEvent): void {
    console.error('[SynapseWorkerBridge] Worker error:', error);
    this.setStatus('error');

    // Reject all pending requests
    this.pendingRequests.forEach((request) => {
      clearTimeout(request.timeout);
      request.reject(new Error('Worker error'));
    });
    this.pendingRequests.clear();
  }

  private setStatus(status: WorkerStatus): void {
    if (this._status !== status) {
      this._status = status;
      this.statusListeners.forEach((listener) => listener(status));
    }
  }

  private startHeartbeatMonitor(): void {
    this.resetHeartbeatMonitor();
  }

  private resetHeartbeatMonitor(): void {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
    }

    this.heartbeatTimeout = setTimeout(() => {
      console.warn('[SynapseWorkerBridge] Heartbeat timeout - restarting worker');
      this.restartWorker();
    }, this.HEARTBEAT_TIMEOUT);
  }

  private stopHeartbeatMonitor(): void {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  private async restartWorker(): Promise<void> {
    this.setStatus('error');

    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    // Clear pending requests
    this.pendingRequests.forEach((request) => {
      clearTimeout(request.timeout);
      request.reject(new Error('Worker restarted'));
    });
    this.pendingRequests.clear();

    // Re-initialize
    try {
      await this.initialize();

      // Re-establish subscriptions
      const oldSubscriptions = new Map(this.subscriptions);
      this.subscriptions.clear();

      for (const [, sub] of oldSubscriptions) {
        // Re-subscribe based on type (we don't have type info stored, so just subscribe to tasks)
        await this.subscribe('tasks', sub.callback);
      }
    } catch (error) {
      console.error('[SynapseWorkerBridge] Failed to restart worker:', error);
    }
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const synapseWorkerBridge = new SynapseWorkerBridge();

// Auto-cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    synapseWorkerBridge.shutdown();
  });
}

export { SynapseWorkerBridge };
